import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import { FormattedMessage } from 'react-intl';
import translations from '../translations';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { injectStripe } from 'react-stripe-elements';
import StripeCardNumberField from '../../StripeComponents/StripeCardNumberField';
import StripeCardExpiryField from '../../StripeComponents/StripeCardExpiryField';
import StripeCardCVCField from '../../StripeComponents/StripeCardCVCField';
import useIntl from '@afosto/utils-react/intl/useIntl';
import FormActions from '@afosto/app-components-react/Form/FormActions';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';

const InjectedCardNumberField = injectStripe(StripeCardNumberField, { withRef: true });
const InjectedCardExpiryField = injectStripe(StripeCardExpiryField, { withRef: true });
const InjectedCardCVCField = injectStripe(StripeCardCVCField, { withRef: true });

const CreditCardOption = ({ setSelectedPaymentMethod, stripe, innerRef, onSubmit }) => {
  const intl = useIntl();
  const [errorState, setErrorState] = useState({
    formError: null,
    cardNumberError: null,
    expiredError: null,
    cvcError: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitPaymentMethod = async event => {
    event.preventDefault();
    setIsSubmitting(true);
    if (stripe) {
      try {
        const { source, error } = await stripe.createSource({
          type: 'card',
        });

        if (error) {
          return Promise.reject(error);
        }

        const { id: sourceId } = source;
        await onSubmit(sourceId);
        return source;
      } catch (err) {
        return Promise.reject(err);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
      return Promise.reject(Error("Stripe.js hasn't loaded yet."));
    }
  };

  const handleChange = fieldError => ({ error = { message: null } }) => {
    setErrorState({ ...errorState, [fieldError]: error.message });
  };

  const { cardNumberError, expiredError, cvcError } = errorState;

  return (
    <form noValidate onSubmit={handleSubmitPaymentMethod} autoComplete="off">
      <LoadingWrapper isLoading={isSubmitting}>
        <Typography variant="subtitle1" component="div">
          <FormattedMessage {...translations.options.creditCard.title} />
        </Typography>
        <InjectedCardNumberField
          onChange={handleChange('cardNumberError')}
          error={!!errorState.cardNumberError}
          helperText={cardNumberError}
          stripe={stripe}
          ref={innerRef}
          label={intl.formatMessage(translations.fields.cardNumber.label)}
          disabled={isSubmitting}
        />
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <InjectedCardExpiryField
              onChange={handleChange('expiredError')}
              error={!!errorState.expiredError}
              helperText={expiredError}
              stripe={stripe}
              ref={innerRef}
              label={intl.formatMessage(translations.fields.expirationDate.label)}
              disabled={isSubmitting}
            />
          </Grid>
          <Grid item xs={4}>
            <InjectedCardCVCField
              onChange={handleChange('cvcError')}
              error={!!errorState.cvcError}
              helperText={cvcError}
              stripe={stripe}
              ref={innerRef}
              label={intl.formatMessage(translations.fields.cvc.label)}
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>
        <FormActions>
          <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
            <FormattedMessage {...translations.formActions.save} />
          </Button>
          <Button onClick={() => setSelectedPaymentMethod(null)} variant="outlined">
            <FormattedMessage {...translations.formActions.back} />
          </Button>
        </FormActions>
      </LoadingWrapper>
    </form>
  );
};

CreditCardOption.propTypes = {
  setSelectedPaymentMethod: PropTypes.func,
  stripe: PropTypes.object,
  innerRef: PropTypes.shape({ current: PropTypes.any }),
  onSubmit: PropTypes.func,
};

CreditCardOption.defaultProps = {
  setSelectedPaymentMethod: undefined,
  stripe: undefined,
  innerRef: undefined,
  onSubmit: undefined,
};

export default CreditCardOption;
